/** SUNTRONIX ONE — Shared Invoice Centre. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ScheduleWhatsAppButton } from "@/components/whatsapp/ScheduleWhatsAppButton";
import { SendWhatsAppButton } from "@/components/whatsapp/WaComposer";
import { WaRecordMessages } from "@/pages/admin/whatsapp/WaOutbox";
import SignaturePad from "@/components/app/SignaturePad";
import { PaymentBankPanel } from "@/components/invoices/PaymentBankPanel";
import {
  FileText, Download, RefreshCcw, Search, Package, Plus, Archive as PackageIcon,
  Trash2, ShieldCheck, BarChart3, Upload as UploadIcon, Settings, Mail,
} from "lucide-react";

const STATUS_TONE = {
  draft: "text-slate-500", issued: "text-sky-700", sent: "text-sky-700",
  partially_paid: "text-amber-700", paid: "text-emerald-700",
  overdue: "text-red-700", void: "text-slate-400", credited: "text-purple-700",
};

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/** URL for the invoice PDF (Suntronix + Customer users hit the same endpoint,
 *  scoped server-side by tenant). */
function invoicePdfUrl(invoiceId) {
  return `${BACKEND}/api/orders/invoices/${invoiceId}/pdf`;
}

export default function InvoiceCentre() {
  const { user } = useAuth();
  const isSuntronix = !user?.master_company_id;
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showPack, setShowPack] = useState(false);
  const [showAgeing, setShowAgeing] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showReminders, setShowReminders] = useState(false);
  const [showStatement, setShowStatement] = useState(false);
  const [showReceivables, setShowReceivables] = useState(false);
  const [creditFor, setCreditFor] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [uploadFor, setUploadFor] = useState(null);
  const [paymentFor, setPaymentFor] = useState(null);
  const [ackFor, setAckFor] = useState(null);
  const [issuing, setIssuing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [list, sm] = await Promise.all([
        api.get("/invoice-centre", { params: { q: q || undefined, status: status || undefined } }),
        api.get("/invoice-centre/summary"),
      ]);
      setRows(list.data.items || []);
      setSummary(sm.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally { setBusy(false); }
  }, [q, status]);

  useEffect(() => { load(); }, [load]);

  const open = async (r) => {
    setSelected(r);
    try {
      const { data } = await api.get(`/invoice-centre/${r.id}`);
      setDetail(data);
    } catch { setDetail(null); }
  };

  const kpis = useMemo(() => {
    if (!summary) return [];
    return [
      { key: "total",    label: "Total",    n: summary.total || 0, tone: "text-slate-900" },
      { key: "issued",   label: "Issued",   n: summary.by_status?.issued || 0, tone: "text-sky-700" },
      { key: "paid",     label: "Paid",     n: summary.by_status?.paid || 0, tone: "text-emerald-700" },
      { key: "overdue",  label: "Overdue",  n: summary.overdue || 0, tone: "text-red-700" },
      { key: "missing",  label: "Missing docs", n: summary.missing_documents || 0, tone: "text-amber-700" },
    ];
  }, [summary]);

  return (
    <div className="space-y-6 max-w-[1400px]" data-testid="invoice-centre">
      <PageHeader
        eyebrow="Commercial · Documents"
        title={isSuntronix ? "Shared Invoice Centre" : "Your invoices"}
        context={isSuntronix
          ? "Every authorised invoice — Suntronix, Master Client and Customer views — with supporting evidence."
          : "Your Suntronix ONE invoices with supporting evidence.  Download PDFs and monthly packs any time."}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="finance" onClick={() => setShowAgeing(true)} data-testid="inv-ageing-btn">
              <BarChart3 className="h-3.5 w-3.5 mr-1.5" /> Ageing
            </Button>
            <Button variant="manage" onClick={() => setShowPack(true)} data-testid="inv-monthly-pack-btn">
              <PackageIcon className="h-3.5 w-3.5 mr-1.5" /> Monthly pack
            </Button>
            {isSuntronix && (
              <>
                <Button variant="finance" onClick={() => setShowReceivables(true)} data-testid="inv-receivables-btn">
                  <BarChart3 className="h-3.5 w-3.5 mr-1.5" /> Receivables
                </Button>
                <Button variant="finance" onClick={() => setShowStatement(true)} data-testid="inv-statement-btn">
                  <Mail className="h-3.5 w-3.5 mr-1.5" /> Send statement
                </Button>
                <Button variant="finance" onClick={() => setShowReminders(true)} data-testid="inv-reminders-btn">
                  <Settings className="h-3.5 w-3.5 mr-1.5" /> Reminders
                </Button>
                <Button variant="create" onClick={() => setShowImport(true)} data-testid="inv-import-btn">
                  <FileText className="h-3.5 w-3.5 mr-1.5" /> Import CSV
                </Button>
                <Button variant="create" onClick={() => setShowNew(true)} data-testid="inv-new-btn">
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> New invoice
                </Button>
              </>
            )}
            <Button variant="outline" onClick={load} disabled={busy}>
              <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {kpis.map((k) => (
          <div key={k.key} className="st-panel p-3"
                data-testid={`inv-kpi-${k.key}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{k.label}</div>
            <div className={`text-2xl font-heading font-semibold tabular-nums ${k.tone}`}>{k.n}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <ScheduleWhatsAppButton kind="invoice" />
        <div className="relative">
          <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2 top-2.5" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Invoice #, PO, order, code…"
            className="pl-7 h-9 text-[13px] max-w-sm"
            data-testid="inv-search" />
        </div>
        <select className="border rounded h-9 px-2 text-[12px]"
          value={status} onChange={(e) => setStatus(e.target.value)}
          data-testid="inv-status">
          <option value="">All statuses</option>
          {["draft","issued","sent","partially_paid","paid","overdue","void","credited"].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <div className="text-[11px] text-slate-500 ml-auto">{rows.length} invoices</div>
      </div>

      <Section title="Invoices" testId="inv-list">
        <div className="st-panel overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Invoice</th>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">PO</th>
                <th className="text-left px-3 py-2">Amount</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => open(r)}
                      data-testid={`inv-row-${r.id}`}>
                  <td className="px-3 py-2 font-medium">
                    <div className="flex items-center gap-1.5">
                      <span>{r.invoice_number || r.code}</span>
                      {r.source === "tally" && (
                        <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 uppercase tracking-wide"
                          data-testid={`inv-tally-tag-${r.id}`}>Tally</span>
                      )}
                    </div>
                    {r.order_number && (
                      <Link to={`/commercial/one-orders?open=${r.order_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-[10px] text-sky-700 hover:underline font-mono"
                        data-testid={`inv-order-link-${r.id}`}>
                        {r.order_number}
                      </Link>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-slate-500">{r.invoice_date}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{r.po_number || "—"}</td>
                  <td className="px-3 py-2 tabular-nums">{r.currency} {(r.grand_total ?? r.amount)?.toLocaleString?.() || (r.grand_total ?? r.amount)}</td>
                  <td className={`px-3 py-2 text-[11px] uppercase tracking-widest font-semibold ${STATUS_TONE[r.status] || "text-slate-500"}`}>
                    {r.status}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <a href={invoicePdfUrl(r.id)} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-sky-700 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`inv-download-${r.id}`}>
                      <Download className="h-3 w-3" /> PDF
                    </a>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && !busy && (
                <tr><td colSpan={6} className="text-center py-8 text-[12px] text-slate-400">
                  No invoices match.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) { setSelected(null); setDetail(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="inv-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">{selected?.invoice_number || selected?.code}
              {selected && <SendWhatsAppButton record={{ kind: "invoice", id: selected.id, code: selected.invoice_number || selected.code }} purpose="PAYMENT" testid="send-whatsapp-invoice" />}
            </DialogTitle>
            <div className="text-[12px] text-slate-500">
              {selected?.invoice_date} · {selected?.currency} {selected?.grand_total ?? selected?.amount}
            </div>
          </DialogHeader>
          {selected && <WaRecordMessages record={{ kind: "invoice", id: selected.id }} />}
          {detail && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-[13px]">
                <F label="PO number">{detail.item?.po_number || "—"}</F>
                <F label="Status">{detail.item?.status}</F>
                <F label="Due">{detail.item?.due_date || "—"}</F>
                <F label="Order">{detail.item?.order_number || detail.item?.order_id || "—"}</F>
              </div>
              {isSuntronix && <PaymentBankPanel invoiceId={detail.item.id} />}

              {detail.item?.line_events?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Line items</div>
                  <div className="border rounded overflow-hidden">
                    <table className="w-full text-[12px]">
                      <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                        <tr>
                          <th className="text-left px-2 py-1.5">Description</th>
                          <th className="text-right px-2 py-1.5">Qty</th>
                          <th className="text-right px-2 py-1.5">Unit</th>
                          <th className="text-right px-2 py-1.5">Tax %</th>
                          <th className="text-right px-2 py-1.5">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.item.line_events.map((ln, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-2 py-1.5">{ln.description}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums">{ln.quantity}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums">{ln.unit_price?.toLocaleString?.()}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums">{ln.tax_rate}%</td>
                            <td className="px-2 py-1.5 text-right tabular-nums font-medium">{ln.total?.toLocaleString?.()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
                  <Package className="h-3 w-3" /> Supporting documents
                </div>
                {detail.supporting_documents?.length ? (
                  <ul className="text-[12px] divide-y divide-slate-100 border rounded">
                    {detail.supporting_documents.map((d) => (
                      <li key={d.id} className="px-3 py-2 flex items-center justify-between">
                        <span>{d.kind} · {d.name}</span>
                        {d.url && (
                          <a href={d.url} target="_blank" rel="noreferrer"
                              className="text-sky-700 hover:underline">Download</a>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-[12px] text-slate-400 border rounded px-3 py-4">
                    No supporting documents attached.
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 flex-wrap">
                {isSuntronix && detail.item?.status === "draft" && (
                  <>
                    <Button variant="manage"
                      onClick={() => setEditFor(detail.item)}
                      data-testid="inv-detail-edit">
                      Edit
                    </Button>
                    <Button variant="finance"
                      disabled={issuing}
                      onClick={async () => {
                        setIssuing(true);
                        try {
                          await api.post(`/invoice-centre/${detail.item.id}/issue`);
                          toast.success("Invoice issued");
                          await load();
                          const { data } = await api.get(`/invoice-centre/${detail.item.id}`);
                          setDetail(data);
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Cannot issue");
                        } finally { setIssuing(false); }
                      }}
                      data-testid="inv-detail-issue">
                      {issuing ? "Issuing…" : "Issue invoice"}
                    </Button>
                  </>
                )}
                {isSuntronix && detail.item?.status !== "credited" && (
                  <Button variant="manage"
                    onClick={() => setUploadFor(detail.item)}
                    data-testid="inv-detail-upload">
                    Upload PDF
                  </Button>
                )}
                {isSuntronix && ["issued","sent","partially_paid","overdue"].includes(detail.item?.status) && (
                  <Button variant="finance"
                    onClick={() => setPaymentFor(detail.item)}
                    data-testid="inv-detail-payment">
                    Record payment
                  </Button>
                )}
                {!isSuntronix && ["issued","sent","partially_paid","overdue"].includes(detail.item?.status) && !detail.item?.acknowledged_at && (
                  <Button
                    onClick={() => setAckFor(detail.item)}
                    data-testid="inv-detail-ack">
                    Acknowledge &amp; accept
                  </Button>
                )}
                {detail.item?.acknowledged_at && (
                  <div className="inline-flex items-start gap-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1.5 rounded"
                          data-testid="inv-detail-ack-badge">
                    <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <div>
                      <div>Acknowledged by <strong>{detail.item.acknowledged_by_name}</strong> · {detail.item.acknowledged_at?.slice(0, 10)}</div>
                      {detail.item.acknowledgement?.signature_image && (
                        <img src={detail.item.acknowledgement.signature_image}
                                alt={`Signature of ${detail.item.acknowledged_by_name}`}
                                className="mt-1 max-h-14 object-contain bg-white border border-emerald-200 rounded p-0.5"
                                data-testid="inv-detail-ack-signature" />
                      )}
                    </div>
                  </div>
                )}
                {isSuntronix && ["issued","sent","partially_paid","paid","overdue"].includes(detail.item?.status) && (
                  <Button variant="finance"
                    onClick={() => setCreditFor(detail.item)}
                    data-testid="inv-detail-credit">
                    Raise credit note
                  </Button>
                )}
                {isSuntronix && !["paid","partially_paid","credited"].includes(detail.item?.status) && (
                  <Button variant="outline"
                    className="text-red-700 hover:text-red-800 hover:bg-red-50"
                    disabled={deleting}
                    onClick={async () => {
                      const isDraft = detail.item?.status === "draft";
                      const msg = isDraft
                        ? `Delete draft ${detail.item.invoice_number}? This cannot be undone.`
                        : `Void ${detail.item.invoice_number}? The row stays for audit but is marked void.`;
                      if (!window.confirm(msg)) return;
                      setDeleting(true);
                      try {
                        await api.delete(`/invoice-centre/${detail.item.id}`);
                        toast.success(isDraft ? "Draft deleted" : "Invoice voided");
                        setSelected(null); setDetail(null);
                        load();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Delete refused");
                      } finally { setDeleting(false); }
                    }}
                    data-testid="inv-detail-delete">
                    {detail.item?.status === "draft" ? "Delete" : "Void"}
                  </Button>
                )}
                <a href={invoicePdfUrl(detail.item?.id || selected?.id)}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[13px] bg-slate-900 text-white px-3 py-2 rounded hover:bg-slate-800"
                    data-testid="inv-detail-download">
                  <FileText className="h-3.5 w-3.5" /> Download invoice PDF
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {showNew && (
        <NewInvoiceDialog
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(); }}
        />
      )}
      {showPack && (
        <MonthlyPackDialog
          isSuntronix={isSuntronix}
          onClose={() => setShowPack(false)}
        />
      )}
      {showAgeing && (
        <AgeingDialog onClose={() => setShowAgeing(false)} />
      )}
      {showImport && (
        <BulkImportDialog
          onClose={() => setShowImport(false)}
          onImported={() => { setShowImport(false); load(); }}
        />
      )}
      {showReminders && (
        <ReminderSettingsDialog onClose={() => setShowReminders(false)} />
      )}
      {showStatement && (
        <StatementDialog onClose={() => setShowStatement(false)} />
      )}
      {showReceivables && (
        <ReceivablesOverviewDialog onClose={() => setShowReceivables(false)} />
      )}
      {creditFor && (
        <CreditNoteDialog invoice={creditFor}
          onClose={() => setCreditFor(null)}
          onCreated={() => { setCreditFor(null); load(); }} />
      )}
      {editFor && (
        <EditInvoiceDialog invoice={editFor}
          onClose={() => setEditFor(null)}
          onSaved={async () => {
            setEditFor(null);
            await load();
            if (selected) {
              const { data } = await api.get(`/invoice-centre/${selected.id}`);
              setDetail(data);
            }
          }} />
      )}
      {uploadFor && (
        <UploadInvoiceDialog invoice={uploadFor}
          onClose={() => setUploadFor(null)}
          onUploaded={async () => {
            setUploadFor(null);
            await load();
            if (selected) {
              const { data } = await api.get(`/invoice-centre/${selected.id}`);
              setDetail(data);
            }
          }} />
      )}
      {paymentFor && (
        <PaymentDialog invoice={paymentFor}
          onClose={() => setPaymentFor(null)}
          onRecorded={async () => {
            setPaymentFor(null);
            await load();
            if (selected) {
              const { data } = await api.get(`/invoice-centre/${selected.id}`);
              setDetail(data);
            }
          }} />
      )}
      {ackFor && (
        <AcknowledgeDialog invoice={ackFor}
          onClose={() => setAckFor(null)}
          onSigned={async () => {
            setAckFor(null);
            await load();
            if (selected) {
              const { data } = await api.get(`/invoice-centre/${selected.id}`);
              setDetail(data);
            }
          }} />
      )}
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-[13px]">{children ?? "—"}</div>
    </div>
  );
}

function ReminderSettingsDialog({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/invoice-centre/config/reminders")
      .then(({ data }) => setCfg(data))
      .catch((e) => { toast.error(e.response?.data?.detail || "Load failed"); onClose(); });
  }, []); // eslint-disable-line
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/invoice-centre/config/reminders", {
        enabled: cfg.enabled, first_reminder_day: Number(cfg.first_reminder_day),
        interval_days: Number(cfg.interval_days), max_reminders: Number(cfg.max_reminders),
        tone: cfg.tone, copy_to_accounts: cfg.copy_to_accounts,
      });
      toast.success("Reminder settings saved");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="inv-reminders-dialog">
        <DialogHeader>
          <DialogTitle>Receivables reminder settings</DialogTitle>
          <div className="text-[12px] text-slate-500">
            Automatic overdue reminders sent from {cfg?.accounts_email || "accounts@suntronix.net"}.
          </div>
        </DialogHeader>
        {!cfg ? <div className="py-8 text-center text-[12px] text-slate-500">Loading…</div> : (
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-[13px]">
              <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => set({ enabled: e.target.checked })}
                data-testid="rem-enabled" />
              Send automatic overdue reminders
            </label>
            <div className="grid grid-cols-3 gap-3">
              <label className="block">
                <div className="text-[11px] text-slate-500 mb-1">First reminder (days overdue)</div>
                <Input type="number" min={0} max={180} value={cfg.first_reminder_day}
                  onChange={(e) => set({ first_reminder_day: e.target.value })} className="h-9 text-[13px]" data-testid="rem-first-day" />
              </label>
              <label className="block">
                <div className="text-[11px] text-slate-500 mb-1">Repeat every (days)</div>
                <Input type="number" min={1} max={180} value={cfg.interval_days}
                  onChange={(e) => set({ interval_days: e.target.value })} className="h-9 text-[13px]" data-testid="rem-interval" />
              </label>
              <label className="block">
                <div className="text-[11px] text-slate-500 mb-1">Max reminders (0 = ∞)</div>
                <Input type="number" min={0} max={50} value={cfg.max_reminders}
                  onChange={(e) => set({ max_reminders: e.target.value })} className="h-9 text-[13px]" data-testid="rem-max" />
              </label>
            </div>
            <label className="block">
              <div className="text-[11px] text-slate-500 mb-1">Tone</div>
              <select value={cfg.tone} onChange={(e) => set({ tone: e.target.value })}
                className="w-full border rounded h-9 px-2 text-[13px]" data-testid="rem-tone">
                <option value="friendly">Friendly — gentle nudge</option>
                <option value="firm">Firm — clear request to settle</option>
                <option value="formal">Formal — official notice</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-[13px]">
              <input type="checkbox" checked={!!cfg.copy_to_accounts} onChange={(e) => set({ copy_to_accounts: e.target.checked })}
                data-testid="rem-copy" />
              Send a copy of each reminder to {cfg.accounts_email}
            </label>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !cfg} data-testid="rem-save">
            {busy ? "Saving…" : "Save settings"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatementDialog({ onClose }) {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [preview, setPreview] = useState(null);
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/master-companies").then(({ data }) =>
      setCustomers(Array.isArray(data) ? data : (data?.items || []))).catch(() => {});
  }, []);
  useEffect(() => {
    if (!customerId) { setPreview(null); return; }
    api.get("/invoice-centre/statements/preview", { params: { master_company_id: customerId } })
      .then(({ data }) => { setPreview(data); setTo(data.suggested_email || ""); })
      .catch((e) => { toast.error(e.response?.data?.detail || "Preview failed"); setPreview(null); });
  }, [customerId]);
  const send = async () => {
    if (!to.includes("@")) return toast.error("Enter a valid recipient email");
    setBusy(true);
    try {
      const { data } = await api.post("/invoice-centre/statements/send", {
        master_company_id: customerId, to, message: message || undefined });
      toast.success(`Statement sent to ${data.to} (${data.count} invoice(s))`);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };
  const cur = preview?.currency || "INR";
  const downloadPdf = async () => {
    try {
      const res = await api.get("/invoice-centre/statements/pdf", {
        params: { master_company_id: customerId }, responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Could not open statement PDF"); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="inv-statement-dialog">
        <DialogHeader>
          <DialogTitle>Send outstanding statement</DialogTitle>
          <div className="text-[12px] text-slate-500">
            Emails the customer every open invoice in one note, from accounts@suntronix.net.
          </div>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">Customer</div>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}
              className="w-full border rounded h-9 px-2 text-[13px]" data-testid="stmt-customer">
              <option value="">— pick a customer —</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.code || c.id}</option>)}
            </select>
          </label>
          {preview && (
            <>
              {preview.count === 0 ? (
                <div className="text-[12px] text-slate-500 border rounded px-3 py-4 text-center">
                  No outstanding invoices — nothing to send.
                </div>
              ) : (
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-[12px]">
                    <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                      <tr>
                        <th className="text-left px-2 py-1.5">Invoice</th>
                        <th className="text-left px-2 py-1.5">Date</th>
                        <th className="text-left px-2 py-1.5">Due</th>
                        <th className="text-right px-2 py-1.5">Outstanding</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((r) => (
                        <tr key={r.invoice_number} className="border-t border-slate-100">
                          <td className="px-2 py-1.5 font-mono">{r.invoice_number}</td>
                          <td className="px-2 py-1.5 text-slate-500">{r.invoice_date}</td>
                          <td className="px-2 py-1.5 text-slate-500">{r.due_date}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{cur} {r.outstanding?.toLocaleString?.()}</td>
                        </tr>
                      ))}
                      <tr className="border-t bg-slate-50 font-semibold">
                        <td className="px-2 py-1.5" colSpan={3}>Total outstanding</td>
                        <td className="px-2 py-1.5 text-right tabular-nums" data-testid="stmt-total">{cur} {preview.total_outstanding?.toLocaleString?.()}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
              <label className="block">
                <div className="text-[11px] text-slate-500 mb-1">Recipient email</div>
                <Input value={to} onChange={(e) => setTo(e.target.value)} className="h-9 text-[13px]" data-testid="stmt-to" />
              </label>
              <label className="block">
                <div className="text-[11px] text-slate-500 mb-1">Message (optional)</div>
                <Input value={message} onChange={(e) => setMessage(e.target.value)} className="h-9 text-[13px]" />
              </label>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {preview && preview.count > 0 && (
            <Button variant="outline" onClick={downloadPdf} data-testid="stmt-pdf">
              <Download className="h-3.5 w-3.5 mr-1.5" /> Preview PDF
            </Button>
          )}
          <Button variant="finance" onClick={send} disabled={busy || !preview || preview.count === 0 || !to.includes("@")}
            data-testid="stmt-send">
            {busy ? "Sending…" : "Send statement"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReceivablesOverviewDialog({ onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/invoice-centre/receivables/overview")
      .then(({ data }) => setD(data))
      .catch((e) => { toast.error(e.response?.data?.detail || "Load failed"); onClose(); });
  }, []); // eslint-disable-line
  const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  const bucketLabel = { not_due: "Not due", "0_30": "1–30d", "31_60": "31–60d", "61_90": "61–90d", "90_plus": "90d+" };
  const bucketTone = { not_due: "bg-slate-100 text-slate-700", "0_30": "bg-amber-100 text-amber-800", "31_60": "bg-orange-100 text-orange-800", "61_90": "bg-rose-100 text-rose-800", "90_plus": "bg-red-200 text-red-900" };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="inv-receivables-dialog">
        <DialogHeader>
          <DialogTitle>Receivables overview</DialogTitle>
          <div className="text-[12px] text-slate-500">As of {d?.as_of || "…"}</div>
        </DialogHeader>
        {!d ? <div className="py-10 text-center text-[12px] text-slate-500">Loading…</div> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="border rounded-lg p-3 bg-slate-50" data-testid="rcv-outstanding">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Outstanding</div>
                <div className="text-lg font-bold tabular-nums">{money(d.outstanding_total)}</div>
              </div>
              <div className="border rounded-lg p-3 bg-rose-50 border-rose-200" data-testid="rcv-overdue">
                <div className="text-[10px] uppercase tracking-wider text-rose-700">Overdue ({d.overdue_count})</div>
                <div className="text-lg font-bold tabular-nums text-rose-900">{money(d.overdue_total)}</div>
              </div>
              <div className="border rounded-lg p-3 bg-emerald-50 border-emerald-200" data-testid="rcv-closures">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700">Closed this month</div>
                <div className="text-lg font-bold tabular-nums text-emerald-900">{d.closures_this_month?.count || 0}</div>
                <div className="text-[10px] text-emerald-700">{money(d.closures_this_month?.value)}</div>
              </div>
              <div className="border rounded-lg p-3 bg-sky-50 border-sky-200" data-testid="rcv-reminders">
                <div className="text-[10px] uppercase tracking-wider text-sky-700">Reminders sent (30d)</div>
                <div className="text-lg font-bold tabular-nums text-sky-900">{d.reminders_30d || 0}</div>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Ageing</div>
              <div className="grid grid-cols-5 gap-2">
                {(d.buckets || []).map((b) => (
                  <div key={b.key} className={`rounded-lg p-2 text-center ${bucketTone[b.key]}`}>
                    <div className="text-[10px] font-semibold">{bucketLabel[b.key]}</div>
                    <div className="text-[12px] font-bold tabular-nums mt-0.5">{money(b.amount)}</div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Top overdue customers</div>
              {(d.top_customers || []).filter((c) => c.overdue > 0).length === 0 ? (
                <div className="text-[12px] text-slate-400 border border-dashed rounded px-3 py-4 text-center">Nothing overdue — all clear.</div>
              ) : (
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-[12px]">
                    <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                      <tr><th className="text-left px-2 py-1.5">Customer</th>
                        <th className="text-right px-2 py-1.5">Overdue</th>
                        <th className="text-right px-2 py-1.5">Outstanding</th></tr>
                    </thead>
                    <tbody>
                      {d.top_customers.filter((c) => c.overdue > 0).map((c) => (
                        <tr key={c.master_company_id} className="border-t border-slate-100">
                          <td className="px-2 py-1.5">{c.customer_name}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-rose-800 font-semibold">{money(c.overdue)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{money(c.outstanding)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// New Invoice dialog — Suntronix finance only.  Creates a DRAFT invoice
// with manual lines; hits POST /api/invoice-centre/manual.
// ---------------------------------------------------------------------------
const EMPTY_LINE = { description: "", quantity: 1, unit_price: 0, tax_rate: 18 };

function NewInvoiceDialog({ onClose, onCreated }) {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [poNumber, setPoNumber] = useState("");
  const [availablePOs, setAvailablePOs] = useState([]);
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dueDays, setDueDays] = useState(30);
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([{ ...EMPTY_LINE }]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/master-companies");
        setCustomers(Array.isArray(data) ? data : (data?.items || []));
      } catch { /* leave empty */ }
    })();
  }, []);

  // Auto-fetch this customer's POs so finance can pick one instead of retyping
  useEffect(() => {
    if (!customerId) { setAvailablePOs([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/customer-po",
                                            { params: { master_company_id: customerId } });
        setAvailablePOs(data?.items || []);
      } catch { setAvailablePOs([]); }
    })();
  }, [customerId]);

  const totals = useMemo(() => {
    let subtotal = 0, tax = 0;
    for (const ln of lines) {
      const net = (Number(ln.quantity) || 0) * (Number(ln.unit_price) || 0);
      const t = net * ((Number(ln.tax_rate) || 0) / 100);
      subtotal += net;
      tax += t;
    }
    return { subtotal: round(subtotal), tax: round(tax), grand: round(subtotal + tax) };
  }, [lines]);

  const setLine = (i, patch) =>
    setLines((s) => s.map((ln, idx) => idx === i ? { ...ln, ...patch } : ln));
  const addLine = () => setLines((s) => [...s, { ...EMPTY_LINE }]);
  const removeLine = (i) => setLines((s) => s.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!customerId) { toast.error("Pick a customer"); return; }
    if (!lines.length) { toast.error("Add at least one line"); return; }
    for (const ln of lines) {
      if (!ln.description?.trim()) { toast.error("Every line needs a description"); return; }
      if (!(ln.quantity > 0)) { toast.error("Quantity must be > 0"); return; }
      if (!(ln.unit_price >= 0)) { toast.error("Unit price must be ≥ 0"); return; }
    }
    setBusy(true);
    try {
      const { data } = await api.post("/invoice-centre/manual", {
        master_company_id: customerId,
        currency,
        po_number: poNumber || undefined,
        invoice_date: invoiceDate,
        due_days: Number(dueDays) || 30,
        lines: lines.map((ln) => ({
          description: ln.description.trim(),
          quantity: Number(ln.quantity),
          unit_price: Number(ln.unit_price),
          tax_rate: Number(ln.tax_rate) || 0,
        })),
        notes: notes || undefined,
      });
      toast.success(`Draft invoice ${data.invoice_number} created`);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create invoice");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="inv-new-dialog">
        <DialogHeader>
          <DialogTitle>New invoice</DialogTitle>
          <div className="text-[12px] text-slate-500">
            Creates a DRAFT invoice for one customer — issue it after review.
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[11px] text-slate-500 mb-1">Customer</div>
              <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}
                className="w-full border rounded h-9 px-2 text-[13px]"
                data-testid="inv-new-customer">
                <option value="">— pick a customer —</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name || c.code || c.id}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <div className="text-[11px] text-slate-500 mb-1">Currency</div>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                className="w-full border rounded h-9 px-2 text-[13px]">
                {["INR","USD","EUR","GBP","SGD","AUD","AED"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-[11px] text-slate-500 mb-1">PO number (optional)</div>
              <Input value={poNumber} onChange={(e) => setPoNumber(e.target.value)}
                     className="h-9 text-[13px]" data-testid="inv-new-po" />
            </label>
            <label className="block">
              <div className="text-[11px] text-slate-500 mb-1">Invoice date</div>
              <Input type="date" value={invoiceDate}
                     onChange={(e) => setInvoiceDate(e.target.value)}
                     className="h-9 text-[13px]" />
            </label>
            <label className="block">
              <div className="text-[11px] text-slate-500 mb-1">Payment terms (days)</div>
              <Input type="number" value={dueDays} min={0} max={180}
                     onChange={(e) => setDueDays(e.target.value)}
                     className="h-9 text-[13px]" />
            </label>
            <label className="block col-span-2">
              <div className="text-[11px] text-slate-500 mb-1">Notes (optional)</div>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                     className="h-9 text-[13px]" />
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Lines</div>
              <Button size="sm" variant="outline" onClick={addLine} data-testid="inv-new-add-line">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add line
              </Button>
            </div>
            <div className="border rounded overflow-hidden">
              <table className="w-full text-[12px]">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                  <tr>
                    <th className="text-left px-2 py-1.5">Description</th>
                    <th className="text-right px-2 py-1.5 w-20">Qty</th>
                    <th className="text-right px-2 py-1.5 w-28">Unit price</th>
                    <th className="text-right px-2 py-1.5 w-20">Tax %</th>
                    <th className="text-right px-2 py-1.5 w-28">Line total</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((ln, i) => {
                    const total = round((Number(ln.quantity) || 0) * (Number(ln.unit_price) || 0)
                                                     * (1 + (Number(ln.tax_rate) || 0) / 100));
                    return (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-2 py-1"><Input value={ln.description}
                          onChange={(e) => setLine(i, { description: e.target.value })}
                          className="h-8 text-[12px]"
                          data-testid={`inv-new-desc-${i}`} /></td>
                        <td className="px-2 py-1"><Input type="number" value={ln.quantity}
                          onChange={(e) => setLine(i, { quantity: e.target.value })}
                          className="h-8 text-[12px] text-right" /></td>
                        <td className="px-2 py-1"><Input type="number" value={ln.unit_price}
                          onChange={(e) => setLine(i, { unit_price: e.target.value })}
                          className="h-8 text-[12px] text-right" /></td>
                        <td className="px-2 py-1"><Input type="number" value={ln.tax_rate}
                          onChange={(e) => setLine(i, { tax_rate: e.target.value })}
                          className="h-8 text-[12px] text-right" /></td>
                        <td className="px-2 py-1 text-right tabular-nums">{total}</td>
                        <td className="px-1 py-1 text-right">
                          {lines.length > 1 && (
                            <button onClick={() => removeLine(i)}
                              className="text-slate-400 hover:text-red-600"
                              aria-label="Remove line">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex items-center justify-end gap-6 text-[13px] tabular-nums">
            <div><span className="text-slate-500 mr-2">Subtotal</span><strong>{currency} {totals.subtotal}</strong></div>
            <div><span className="text-slate-500 mr-2">Tax</span><strong>{currency} {totals.tax}</strong></div>
            <div className="text-slate-900"><span className="text-slate-500 mr-2">Grand total</span>
              <strong className="text-[15px]">{currency} {totals.grand}</strong></div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="create" onClick={submit} disabled={busy} data-testid="inv-new-submit">
            {busy ? "Creating…" : "Create draft invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function round(n) { return Math.round(n * 100) / 100; }

// ---------------------------------------------------------------------------
// Monthly pack dialog — POST /api/invoice-centre/monthly-pack.zip
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Payment dialog — Suntronix finance records a payment against an issued invoice.
// ---------------------------------------------------------------------------
function PaymentDialog({ invoice, onClose, onRecorded }) {
  const outstanding = round((invoice.grand_total ?? invoice.amount ?? 0)
                                          - (invoice.paid_amount || 0));
  const [amount, setAmount] = useState(outstanding);
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState("bank_transfer");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!(Number(amount) > 0)) { toast.error("Amount must be > 0"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/invoice-centre/${invoice.id}/payments`, {
        amount: Number(amount), payment_date: paymentDate,
        method, reference: reference || null, notes: notes || null,
      });
      toast.success(`${data.status === "paid" ? "Invoice paid in full" : "Payment recorded"}`);
      onRecorded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="inv-payment-dialog">
        <DialogHeader>
          <DialogTitle>Record payment · {invoice.invoice_number}</DialogTitle>
          <div className="text-[12px] text-slate-500">
            Outstanding: <strong className="tabular-nums">{invoice.currency} {outstanding.toLocaleString?.()}</strong>
          </div>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">Amount ({invoice.currency})</div>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                     className="h-9 text-[13px]" data-testid="inv-payment-amount" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[11px] text-slate-500 mb-1">Payment date</div>
              <Input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)}
                       className="h-9 text-[13px]" />
            </label>
            <label className="block">
              <div className="text-[11px] text-slate-500 mb-1">Method</div>
              <select value={method} onChange={(e) => setMethod(e.target.value)}
                className="w-full border rounded h-9 px-2 text-[13px]">
                {["bank_transfer","cheque","cash","upi","card","adjustment","other"].map((m) => <option key={m}>{m}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">Reference (bank / cheque #)</div>
            <Input value={reference} onChange={(e) => setReference(e.target.value)}
                     className="h-9 text-[13px]" />
          </label>
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">Notes</div>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                     className="h-9 text-[13px]" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="inv-payment-submit">
            {busy ? "Recording…" : "Record payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Acknowledge dialog — customer draws their signature on a canvas.
// ---------------------------------------------------------------------------
function AcknowledgeDialog({ invoice, onClose, onSigned }) {
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [remarks, setRemarks] = useState("");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const padRef = useRef(null);

  const submit = async () => {
    if (!name || name.trim().length < 2) { toast.error("Please type your full name"); return; }
    if (!dirty || padRef.current?.isEmpty()) {
      toast.error("Please draw your signature in the box");
      return;
    }
    setBusy(true);
    try {
      const signature_image = padRef.current.toDataURL("image/png");
      await api.post(`/invoice-centre/${invoice.id}/acknowledge`, {
        signature_name: name.trim(),
        signature_title: title.trim() || null,
        signature_image,
        remarks: remarks || null,
        accept: true,
      });
      toast.success("Invoice acknowledged");
      onSigned?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Acknowledgement failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="inv-ack-dialog">
        <DialogHeader>
          <DialogTitle>Acknowledge {invoice.invoice_number}</DialogTitle>
          <div className="text-[12px] text-slate-500">
            Draw your signature below and confirm receipt of this invoice.  Suntronix and your finance team see this immediately.
          </div>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[11px] text-slate-500 mb-1">Full name</div>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                       className="h-9 text-[13px]"
                       placeholder="Your full name"
                       data-testid="inv-ack-name" />
            </label>
            <label className="block">
              <div className="text-[11px] text-slate-500 mb-1">Title (optional)</div>
              <Input value={title} onChange={(e) => setTitle(e.target.value)}
                       className="h-9 text-[13px]" placeholder="Finance Manager…" />
            </label>
          </div>
          <div>
            <div className="text-[11px] text-slate-500 mb-1 flex items-center justify-between">
              <span>Draw your signature</span>
              <button type="button"
                onClick={() => { padRef.current?.clear(); setDirty(false); }}
                className="text-[11px] text-slate-500 hover:text-slate-800 underline"
                data-testid="inv-ack-clear">
                Clear
              </button>
            </div>
            <SignaturePad ref={padRef} height={170} onDirtyChange={setDirty} />
          </div>
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">Remarks (optional)</div>
            <Input value={remarks} onChange={(e) => setRemarks(e.target.value)}
                     className="h-9 text-[13px]" />
          </label>
          <div className="text-[11px] text-slate-500 bg-slate-50 border rounded p-2">
            By signing below you e-sign this invoice on behalf of your organisation.
            Timestamp, IP and your account email are captured for the audit trail.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="inv-ack-submit">
            {busy ? "Signing…" : "Acknowledge & accept"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Edit Invoice — draft-only.  Reuses PATCH /api/invoice-centre/{id}.
// ---------------------------------------------------------------------------
function EditInvoiceDialog({ invoice, onClose, onSaved }) {
  const [poNumber, setPoNumber] = useState(invoice.po_number || "");
  const [invoiceDate, setInvoiceDate] = useState(invoice.invoice_date || "");
  const [dueDate, setDueDate] = useState(invoice.due_date || "");
  const [notes, setNotes] = useState(invoice.notes || "");
  const [lines, setLines] = useState(() => (invoice.line_events || []).map((ln) => ({
    description: ln.description || "",
    quantity: ln.quantity ?? 1,
    unit_price: ln.unit_price ?? 0,
    tax_rate: ln.tax_rate ?? 0,
  })));
  const [busy, setBusy] = useState(false);

  const setLine = (i, patch) =>
    setLines((s) => s.map((ln, idx) => idx === i ? { ...ln, ...patch } : ln));
  const addLine = () => setLines((s) => [...s, { description: "", quantity: 1, unit_price: 0, tax_rate: 0 }]);
  const removeLine = (i) => setLines((s) => s.filter((_, idx) => idx !== i));

  const totals = useMemo(() => {
    let subtotal = 0, tax = 0;
    for (const ln of lines) {
      const net = (Number(ln.quantity) || 0) * (Number(ln.unit_price) || 0);
      subtotal += net;
      tax += net * ((Number(ln.tax_rate) || 0) / 100);
    }
    return { subtotal: round(subtotal), tax: round(tax), grand: round(subtotal + tax) };
  }, [lines]);

  const submit = async () => {
    if (!lines.length) { toast.error("At least one line is required"); return; }
    for (const ln of lines) {
      if (!ln.description?.trim()) { toast.error("Every line needs a description"); return; }
      if (!(ln.quantity > 0)) { toast.error("Quantity must be > 0"); return; }
    }
    setBusy(true);
    try {
      await api.patch(`/invoice-centre/${invoice.id}`, {
        po_number: poNumber || null,
        invoice_date: invoiceDate,
        due_date: dueDate,
        notes: notes || null,
        lines: lines.map((ln) => ({
          description: ln.description.trim(),
          quantity: Number(ln.quantity),
          unit_price: Number(ln.unit_price),
          tax_rate: Number(ln.tax_rate) || 0,
        })),
      });
      toast.success("Invoice updated");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="inv-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit {invoice.invoice_number}</DialogTitle>
          <div className="text-[12px] text-slate-500">Only DRAFT invoices are editable.</div>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[11px] text-slate-500 mb-1">PO number</div>
              <Input value={poNumber} onChange={(e) => setPoNumber(e.target.value)}
                       className="h-9 text-[13px]" data-testid="inv-edit-po" />
            </label>
            <label className="block">
              <div className="text-[11px] text-slate-500 mb-1">Invoice date</div>
              <Input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)}
                       className="h-9 text-[13px]" />
            </label>
            <label className="block">
              <div className="text-[11px] text-slate-500 mb-1">Due date</div>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                       className="h-9 text-[13px]" />
            </label>
            <label className="block">
              <div className="text-[11px] text-slate-500 mb-1">Notes</div>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                       className="h-9 text-[13px]" />
            </label>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Lines</div>
              <Button size="sm" variant="outline" onClick={addLine}>
                <Plus className="h-3.5 w-3.5 mr-1" /> Add line
              </Button>
            </div>
            <div className="border rounded overflow-hidden">
              <table className="w-full text-[12px]">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                  <tr>
                    <th className="text-left px-2 py-1.5">Description</th>
                    <th className="text-right px-2 py-1.5 w-20">Qty</th>
                    <th className="text-right px-2 py-1.5 w-28">Unit price</th>
                    <th className="text-right px-2 py-1.5 w-20">Tax %</th>
                    <th className="text-right px-2 py-1.5 w-28">Total</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((ln, i) => {
                    const total = round((Number(ln.quantity) || 0) * (Number(ln.unit_price) || 0)
                                                     * (1 + (Number(ln.tax_rate) || 0) / 100));
                    return (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-2 py-1"><Input value={ln.description}
                          onChange={(e) => setLine(i, { description: e.target.value })}
                          className="h-8 text-[12px]" /></td>
                        <td className="px-2 py-1"><Input type="number" value={ln.quantity}
                          onChange={(e) => setLine(i, { quantity: e.target.value })}
                          className="h-8 text-[12px] text-right" /></td>
                        <td className="px-2 py-1"><Input type="number" value={ln.unit_price}
                          onChange={(e) => setLine(i, { unit_price: e.target.value })}
                          className="h-8 text-[12px] text-right" /></td>
                        <td className="px-2 py-1"><Input type="number" value={ln.tax_rate}
                          onChange={(e) => setLine(i, { tax_rate: e.target.value })}
                          className="h-8 text-[12px] text-right" /></td>
                        <td className="px-2 py-1 text-right tabular-nums">{total}</td>
                        <td className="px-1 py-1 text-right">
                          {lines.length > 1 && (
                            <button onClick={() => removeLine(i)}
                              className="text-slate-400 hover:text-red-600"
                              aria-label="Remove line">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex items-center justify-end gap-6 text-[13px] tabular-nums">
            <div><span className="text-slate-500 mr-2">Subtotal</span><strong>{invoice.currency} {totals.subtotal}</strong></div>
            <div><span className="text-slate-500 mr-2">Tax</span><strong>{invoice.currency} {totals.tax}</strong></div>
            <div><span className="text-slate-500 mr-2">Grand total</span>
              <strong className="text-[15px]">{invoice.currency} {totals.grand}</strong></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="inv-edit-submit">
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Upload Invoice PDF/image — attaches a signed document to any invoice.
// ---------------------------------------------------------------------------
function UploadInvoiceDialog({ invoice, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/invoice-centre/${invoice.id}/upload`, form,
                            { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${file.name}`);
      onUploaded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="inv-upload-dialog">
        <DialogHeader>
          <DialogTitle>Upload PDF for {invoice.invoice_number}</DialogTitle>
          <div className="text-[12px] text-slate-500">
            Attach a signed / countersigned PDF (max 25 MB, PDF · PNG · JPG · WEBP).
          </div>
        </DialogHeader>
        <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="block w-full text-[13px]"
                  data-testid="inv-upload-file" />
        {file && (
          <div className="text-[12px] text-slate-500">
            {file.name} · {Math.round(file.size / 1024)} KB
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !file} data-testid="inv-upload-submit">
            {busy ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Credit Note dialog — reverses an issued invoice (full or partial).
// ---------------------------------------------------------------------------
function CreditNoteDialog({ invoice, onClose, onCreated }) {
  const [reason, setReason] = useState("");
  const [mode, setMode] = useState("full");   // full | partial
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason || reason.trim().length < 3) { toast.error("Reason must be at least 3 characters"); return; }
    if (mode === "partial" && !(Number(amount) > 0)) { toast.error("Enter a positive credit amount"); return; }
    setBusy(true);
    try {
      const body = { reason: reason.trim() };
      if (mode === "partial") body.override_amount = Number(amount);
      const { data } = await api.post(`/invoice-centre/${invoice.id}/credit-note`, body);
      toast.success(`Credit note ${data.invoice_number} raised`);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Credit note failed");
    } finally { setBusy(false); }
  };

  const currency = invoice.currency;
  const grand = invoice.grand_total ?? invoice.amount ?? 0;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="inv-credit-dialog">
        <DialogHeader>
          <DialogTitle>Credit note against {invoice.invoice_number}</DialogTitle>
          <div className="text-[12px] text-slate-500">
            Reverses this invoice — same PDF format, cross-referenced automatically.
          </div>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-[12px] text-slate-600">
            Invoice total: <strong className="tabular-nums">{currency} {grand.toLocaleString?.() ?? grand}</strong>
          </div>
          <div className="flex items-center gap-4 text-[13px]">
            <label className="inline-flex items-center gap-1.5">
              <input type="radio" checked={mode === "full"} onChange={() => setMode("full")}
                     data-testid="inv-credit-mode-full" />
              Full reversal
            </label>
            <label className="inline-flex items-center gap-1.5">
              <input type="radio" checked={mode === "partial"} onChange={() => setMode("partial")}
                     data-testid="inv-credit-mode-partial" />
              Partial credit
            </label>
          </div>
          {mode === "partial" && (
            <label className="block">
              <div className="text-[11px] text-slate-500 mb-1">Credit amount ({currency})</div>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                     className="h-9 text-[13px]" data-testid="inv-credit-amount" />
            </label>
          )}
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">Reason</div>
            <Input value={reason} onChange={(e) => setReason(e.target.value)}
                     className="h-9 text-[13px]"
                     placeholder="Duplicate billing / service dispute / contract adjustment"
                     data-testid="inv-credit-reason" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="inv-credit-submit">
            {busy ? "Raising…" : "Raise credit note"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Ageing dashboard — 0-30 / 31-60 / 61-90 / 90+ heatmap.
// ---------------------------------------------------------------------------
const BUCKET_LABELS = {
  not_due: "Not due", "0_30": "0-30 days", "31_60": "31-60 days",
  "61_90": "61-90 days", "90_plus": "90+ days",
};
const BUCKET_TONE = {
  not_due: "bg-slate-100 text-slate-700",
  "0_30": "bg-amber-100 text-amber-800",
  "31_60": "bg-orange-100 text-orange-800",
  "61_90": "bg-red-100 text-red-800",
  "90_plus": "bg-red-200 text-red-900 font-semibold",
};

function AgeingDialog({ onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/invoice-centre/ageing"); setData(data); }
      catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
      finally { setBusy(false); }
    })();
  }, []);
  const totalOutstanding = data?.buckets?.reduce((s, b) => s + (b.amount || 0), 0) || 0;
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="inv-ageing-dialog">
        <DialogHeader>
          <DialogTitle>Invoice ageing</DialogTitle>
          <div className="text-[12px] text-slate-500">
            Bucketed by days past due · {data?.as_of || "…"}
          </div>
        </DialogHeader>
        {busy ? (
          <div className="text-center text-[12px] text-slate-500 py-8">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {data?.buckets?.map((b) => (
                <div key={b.key} className={`rounded p-3 ${BUCKET_TONE[b.key] || "bg-slate-100"}`}
                        data-testid={`inv-ageing-bucket-${b.key}`}>
                  <div className="text-[10px] uppercase tracking-wider opacity-80">{BUCKET_LABELS[b.key] || b.key}</div>
                  <div className="text-2xl font-heading font-semibold tabular-nums">{b.count}</div>
                  <div className="text-[11px] tabular-nums opacity-80">{b.amount?.toLocaleString?.()}</div>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-slate-500">
              Total outstanding across all buckets: <strong className="tabular-nums">{totalOutstanding.toLocaleString()}</strong>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Top overdue customers</div>
              <div className="border rounded overflow-hidden">
                <table className="w-full text-[12px]">
                  <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                    <tr>
                      <th className="text-left px-2 py-1.5">Customer</th>
                      <th className="text-right px-2 py-1.5">Invoices</th>
                      <th className="text-right px-2 py-1.5">0-30</th>
                      <th className="text-right px-2 py-1.5">31-60</th>
                      <th className="text-right px-2 py-1.5">61-90</th>
                      <th className="text-right px-2 py-1.5">90+</th>
                      <th className="text-right px-2 py-1.5">Outstanding</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.by_customer || []).map((c) => (
                      <tr key={c.master_company_id} className="border-t border-slate-100"
                            data-testid={`inv-ageing-cust-${c.master_company_id}`}>
                        <td className="px-2 py-1.5">
                          <div className="flex items-center gap-2">
                            <span className={`inline-block h-2 w-2 rounded-full ${BUCKET_TONE[c.worst_bucket]}`} />
                            {c.customer_name}
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{c.invoice_count}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-amber-800">{c["0_30"] > 0 ? c["0_30"].toLocaleString() : "—"}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-orange-800">{c["31_60"] > 0 ? c["31_60"].toLocaleString() : "—"}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-red-800">{c["61_90"] > 0 ? c["61_90"].toLocaleString() : "—"}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-red-900 font-semibold">{c["90_plus"] > 0 ? c["90_plus"].toLocaleString() : "—"}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-medium">{c.currency} {c.total_outstanding?.toLocaleString?.()}</td>
                      </tr>
                    ))}
                    {(!data?.by_customer || data.by_customer.length === 0) && (
                      <tr><td colSpan={7} className="px-2 py-6 text-center text-slate-400">
                        No outstanding invoices — everything is settled.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Bulk import dialog — Suntronix finance uploads a CSV of legacy invoices.
// Expected columns: invoice_number, customer_code, po_number, currency,
// invoice_date, due_date, description, quantity, unit_price, tax_rate, status.
// ---------------------------------------------------------------------------
const IMPORT_TEMPLATE_HEADERS = [
  "invoice_number","customer_code","master_company_id","po_number","currency",
  "invoice_date","due_date","description","quantity","unit_price","tax_rate","status",
];

function BulkImportDialog({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [dryRun, setDryRun] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const parseCsv = async (f) => {
    const text = await f.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) { toast.error("CSV must have a header row + at least one data row"); return; }
    const header = lines[0].split(",").map((h) => h.trim());
    const out = [];
    for (let i = 1; i < lines.length; i++) {
      // very lightweight CSV parser — no quoted-comma support (finance
      // exports typically don't need it; suggest they escape descriptions).
      const cells = lines[i].split(",").map((c) => c.trim());
      const row = {};
      header.forEach((h, idx) => { row[h] = cells[idx] ?? ""; });
      // Coerce numeric cols
      row.quantity = Number(row.quantity || 1);
      row.unit_price = Number(row.unit_price || 0);
      row.tax_rate = Number(row.tax_rate || 0);
      out.push(row);
    }
    setRows(out);
    setResult(null);
    toast.success(`Parsed ${out.length} rows`);
  };

  const submit = async () => {
    if (!rows.length) { toast.error("Load a CSV first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/invoice-centre/import",
                                                { rows, dry_run: dryRun });
      setResult(data);
      if (dryRun) {
        toast.success(`Dry run: ${data.errors} errors on ${data.total_rows} rows`);
      } else {
        toast.success(`Imported ${data.created} invoices (${data.errors} errors)`);
        if (data.created > 0) onImported?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const blob = new Blob([IMPORT_TEMPLATE_HEADERS.join(",") + "\n"], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "invoice-import-template.csv";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="inv-import-dialog">
        <DialogHeader>
          <DialogTitle>Bulk invoice import</DialogTitle>
          <div className="text-[12px] text-slate-500">
            Absorb legacy invoices in one shot.  Every row uses the shared invoice sequence and audit trail.
          </div>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <input type="file" accept=".csv,text/csv"
                     onChange={(e) => {
                       const f = e.target.files?.[0];
                       setFile(f || null);
                       if (f) parseCsv(f);
                     }}
                     data-testid="inv-import-file"
                     className="text-[13px]" />
            <Button size="sm" variant="outline" onClick={downloadTemplate}
                        data-testid="inv-import-template">
              <Download className="h-3.5 w-3.5 mr-1" /> Template CSV
            </Button>
          </div>
          {file && (
            <div className="text-[12px] text-slate-500">
              {file.name} · {rows.length} rows parsed
            </div>
          )}
          <label className="inline-flex items-center gap-2 text-[12px]">
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)}
                     data-testid="inv-import-dryrun" />
            Dry run (validate only — no rows written)
          </label>
          {rows.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Preview (first 5 rows)</div>
              <div className="border rounded overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-50 text-[9px] uppercase text-slate-500">
                    <tr>
                      {IMPORT_TEMPLATE_HEADERS.map((h) => <th key={h} className="px-2 py-1.5 text-left">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 5).map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        {IMPORT_TEMPLATE_HEADERS.map((h) => (
                          <td key={h} className="px-2 py-1 truncate max-w-[120px]">{r[h] ?? ""}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {result && (
            <div className={`rounded border p-3 text-[12px] ${result.errors === 0 ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}
                    data-testid="inv-import-result">
              <div><strong>{result.dry_run ? "Dry run" : "Import"} complete</strong> · {result.total_rows} rows · {result.created} created · {result.errors} errors</div>
              {result.errors > 0 && (
                <ul className="mt-2 list-disc pl-5">
                  {result.results.filter((r) => !r.ok).slice(0, 10).map((r) => (
                    <li key={r.row}>Row {r.row}: {r.error}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={submit} disabled={busy || !rows.length} data-testid="inv-import-submit">
            {busy ? "Working…" : (dryRun ? "Run validation" : "Import invoices")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MonthlyPackDialog({ isSuntronix, onClose }) {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [customerId, setCustomerId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isSuntronix) return;
    (async () => {
      try {
        const { data } = await api.get("/master-companies");
        setCustomers(Array.isArray(data) ? data : (data?.items || []));
      } catch { /* ignore */ }
    })();
  }, [isSuntronix]);

  const download = async () => {
    if (!month) { toast.error("Pick a month"); return; }
    setBusy(true);
    try {
      const body = { month };
      if (isSuntronix && customerId) body.master_company_id = customerId;
      const res = await api.post("/invoice-centre/monthly-pack.zip", body,
                                            { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-pack-${month}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded monthly pack");
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to build pack");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="inv-pack-dialog">
        <DialogHeader>
          <DialogTitle>Monthly invoice pack</DialogTitle>
          <div className="text-[12px] text-slate-500">
            Downloads a ZIP of every invoice + supporting document for the chosen month.
          </div>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">Month</div>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                     className="h-9 text-[13px]" data-testid="inv-pack-month" />
          </label>
          {isSuntronix && (
            <label className="block">
              <div className="text-[11px] text-slate-500 mb-1">Customer (optional)</div>
              <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}
                className="w-full border rounded h-9 px-2 text-[13px]">
                <option value="">All customers</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.code}</option>)}
              </select>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={download} disabled={busy} data-testid="inv-pack-download">
            {busy ? "Building…" : "Download ZIP"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
